import React from "react";
import { useSearchBox, useRefinementList, useSortBy, useClearRefinements, useHits, useStats, usePagination } from "react-instantsearch";
import { Input, Button } from "reactstrap";

// Custom SearchBox component
export const CustomSearchBox = ({ queryFields, placeholder = "Buscar" }) => {
  const { query, refine } = useSearchBox();

  return (
    <Input
      type="text"
      placeholder={placeholder}
      value={query}
      onChange={(e) => refine(e.target.value)}
      autoFocus
    />
  );
};

// Custom Stats component
export const CustomStats = () => {
  const { nbHits, processingTimeMS } = useStats();
  
  return (
    <div className="small text-muted mt-2">
      {nbHits || 0} resultados encontrados en {processingTimeMS || 0}ms
    </div>
  );
};

// Custom SortBy component
export const CustomSortBy = ({ options, onSortChange }) => {
  const [currentSort, setCurrentSort] = React.useState(
    options.find(o => o.defaultOption) || options[0]
  );

  const handleChange = (e) => {
    const selectedOption = options.find(opt => 
      `${opt.field}:${opt.order}` === e.target.value
    );
    if (selectedOption) {
      setCurrentSort(selectedOption);
      if (onSortChange) {
        onSortChange(selectedOption);
      }
    }
  };

  return (
    <select
      className="form-select form-select-sm"
      value={`${currentSort.field}:${currentSort.order}`}
      onChange={handleChange}
    >
      {options.map((opt, idx) => (
        <option key={idx} value={`${opt.field}:${opt.order}`}>
          {opt.label}
        </option>
      ))}
    </select>
  );
};

// Custom RefinementList component
export const CustomRefinementList = ({ 
  attribute, 
  searchable = true, 
  searchPlaceholder = "Buscar...",
  itemComponent,
  showMore = false,
  limit = 100,
  onRefine
}) => {
  const { items, refine, searchForItems, query } = useRefinementList({
    attribute,
    searchable,
    limit
  });

  const handleRefine = (value) => {
    refine(value);
    if (onRefine) {
      onRefine(value);
    }
  };

  return (
    <div>
      {searchable && (
        <Input
          type="text"
          placeholder={searchPlaceholder}
          value={query}
          onChange={(e) => searchForItems(e.target.value)}
          className="mb-2"
          bsSize="sm"
        />
      )}
      <div>
        {items.map((item) => {
          const ItemComponent = itemComponent || DefaultRefinementItem;
          // If itemComponent is provided, it expects { label, count, active, disabled, onClick }
          // Otherwise, use the default format { item, refine }
          if (itemComponent) {
            return (
              <ItemComponent
                key={item.value || item.label}
                label={item.label}
                count={item.count}
                active={item.isRefined}
                disabled={item.count === 0}
                onClick={() => handleRefine(item.value)}
              />
            );
          } else {
            return (
              <ItemComponent
                key={item.label}
                item={item}
                refine={handleRefine}
              />
            );
          }
        })}
      </div>
    </div>
  );
};

// Default refinement item component
const DefaultRefinementItem = ({ item, refine }) => {
  return (
    <div
      className={`sk-refinement-option${item.isRefined ? " is-active" : ""}`}
      style={{ cursor: "pointer", padding: "4px 0" }}
      onClick={() => refine(item.value)}
    >
      <input
        type="checkbox"
        readOnly
        checked={item.isRefined}
        style={{ marginRight: 8 }}
      />
      <span>{item.label}</span>
      <span style={{ float: "right", fontWeight: 500, minWidth: 20, textAlign: "right" }}>
        {item.count ?? 0}
      </span>
    </div>
  );
};

// Custom ClearRefinements component
export const CustomClearRefinements = ({ translations = {} }) => {
  const { refine, canRefine } = useClearRefinements();

  if (!canRefine) return null;

  return (
    <Button
      color="link"
      size="sm"
      onClick={() => refine()}
      className="p-0"
    >
      {translations["reset.clear_all"] || "Limpiar filtros"}
    </Button>
  );
};

// Custom Pagination component
// Note: usePagination uses 0-based indexing internally, but we display 1-based to users
export const CustomPagination = () => {
  const { nbPages, refine, currentRefinement } = usePagination({
    padding: 2
  });

  console.log('[CustomPagination] State:', { 
    nbPages, 
    currentRefinement, 
    displayPage: currentRefinement + 1,
    refineFunction: typeof refine
  });

  if (nbPages <= 1) return null;

  // Convert 0-based to 1-based for display
  const currentPage = currentRefinement + 1;
  const totalPages = nbPages;

  const pages = [];
  const padding = 2;
  const startPage = Math.max(1, currentPage - padding);
  const endPage = Math.min(totalPages, currentPage + padding);

  if (startPage > 1) {
    pages.push(1);
    if (startPage > 2) pages.push('...');
  }

  for (let i = startPage; i <= endPage; i++) {
    pages.push(i);
  }

  if (endPage < totalPages) {
    if (endPage < totalPages - 1) pages.push('...');
    pages.push(totalPages);
  }

  const handlePageClick = (pageNum) => {
    // Convert 1-based (display) to 0-based (refine expects 0-based)
    // If user clicks page 1, we want refine(0)
    // If user clicks page 2, we want refine(1)
    const zeroBasedPage = pageNum - 1;
    console.log('[CustomPagination] Click:', { 
      clickedPage: pageNum, 
      zeroBasedPage, 
      currentRefinement,
      currentDisplayPage: currentRefinement + 1,
      willCallRefineWith: zeroBasedPage,
      nbPages
    });
    
    // CRITICAL: If we're already on the page, don't call refine
    // This might prevent duplicate calls
    if (zeroBasedPage === currentRefinement) {
      console.log('[CustomPagination] Already on this page, skipping refine');
      return;
    }
    
    refine(zeroBasedPage);
  };

  return (
    <div className="d-flex justify-content-center align-items-center gap-2 mt-3">
      <Button
        color="secondary"
        size="sm"
        outline
        disabled={currentPage === 1}
        onClick={() => handlePageClick(1)}
      >
        « Primero
      </Button>
      <Button
        color="secondary"
        size="sm"
        outline
        disabled={currentPage === 1}
        onClick={() => handlePageClick(currentPage - 1)}
      >
        ‹ Anterior
      </Button>
      
      {pages.map((page, idx) => {
        if (page === '...') {
          return <span key={`ellipsis-${idx}`} className="px-2">…</span>;
        }
        return (
          <Button
            key={page}
            color={page === currentPage ? "primary" : "secondary"}
            size="sm"
            outline={page !== currentPage}
            onClick={() => handlePageClick(page)}
          >
            {page}
          </Button>
        );
      })}
      
      <Button
        color="secondary"
        size="sm"
        outline
        disabled={currentPage === totalPages}
        onClick={() => handlePageClick(currentPage + 1)}
      >
        Siguiente ›
      </Button>
      <Button
        color="secondary"
        size="sm"
        outline
        disabled={currentPage === totalPages}
        onClick={() => handlePageClick(totalPages)}
      >
        Último »
      </Button>
    </div>
  );
};

